import { useEffect, useRef, useState } from "react";
import emailjs from "@emailjs/browser";
import { FaPlus } from "react-icons/fa";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Footer from "../components/Footer";
import MenuDesktopHorizontal from "../components/MenuDesktopHorizontal";
import { client } from "../client";

const fallbackVideos = [
  {
    title: "Quand tu es prêt à tout pour réaliser ton rêve mais...",
    description: "Une animation courte et motivante sur la persévérance.",
    videoId: "hsl1sdGMQqw",
    category: "Motivation",
  },
  {
    title: "Quand tu crois que ton rêve est réel",
    description: "Une situation drôle racontée en animation 2D.",
    videoId: "TbJop1alclk",
    category: "Humour",
  },
  {
    title: "Épisode 3 : la vie d'informaticien",
    description: "Le quotidien d'un informaticien raconté avec humour.",
    videoId: "ioTDc_alxA8",
    category: "Série",
  },
  {
    title: "Épisode 2 : la vie d'informaticien",
    description: "Une nouvelle scène de la série sur la vie d'informaticien.",
    videoId: "AdfKPommJHE",
    category: "Série",
  },
  {
    title: "Épisode 1 : la vie d'informaticien",
    description: "Le premier épisode de cette série d'animations 2D.",
    videoId: "MV52wbCbcOM",
    category: "Série",
  },
  {
    title: "Wow, tu as vraiment des muscles !",
    description: "Une scène de couple amusante en animation.",
    videoId: "XpvEJmocVpY",
    category: "Couple",
  },
  {
    title: "Tu connais cette sensation...",
    description: "Une courte animation inspirée du dessin et de la vie quotidienne.",
    videoId: "qNzsvUpN31Q",
    category: "Animation 2D",
  },
  {
    title: "Quand Google est musulman",
    description: "Une animation humoristique inspirée d'une situation du quotidien.",
    videoId: "YYauSbc14mk",
    category: "Humour",
  },
];

const getVideoCategory = (title) => {
  const normalizedTitle = title.toLowerCase();

  if (normalizedTitle.includes("motivation") || normalizedTitle.includes("mindset")) {
    return "Motivation";
  }

  if (normalizedTitle.includes("informaticien") || normalizedTitle.includes("informatique")) {
    return "Série";
  }

  if (normalizedTitle.includes("couple") || normalizedTitle.includes("muscles")) {
    return "Couple";
  }

  if (normalizedTitle.includes("google") || normalizedTitle.includes("rêve")) {
    return "Humour";
  }

  return "Animation 2D";
};

const Animation = () => {
  const [videos, setVideos] = useState(fallbackVideos);
  const [nom, setNom] = useState("");
  const [email, setEmail] = useState("");
  const [objectif, setObjectif] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const form = useRef(null);

  useEffect(() => {
    const feedUrl =
      "https://api.rss2json.com/v1/api.json?rss_url=https%3A%2F%2Fwww.youtube.com%2Ffeeds%2Fvideos.xml%3Fchannel_id%3DUCrEJ124isf9JDL2p4HD_57g";

    fetch(feedUrl)
      .then((response) => {
        if (!response.ok) {
          throw new Error("Impossible de charger les vidéos YouTube");
        }
        return response.json();
      })
      .then((data) => {
        if (data.status !== "ok" || !Array.isArray(data.items)) {
          throw new Error("Flux YouTube indisponible");
        }

        const latestVideos = data.items.slice(0, 8).map((item) => {
          const videoId = item.guid.split(":").pop();

          return {
            title: item.title,
            description: "Une nouvelle animation 2D publiée sur ma chaîne YouTube.",
            videoId,
            category: getVideoCategory(item.title),
            thumbnail: item.thumbnail,
            link: item.link,
          };
        });

        if (latestVideos.length > 0) {
          setVideos(latestVideos);
        }
      })
      .catch(() => {
        // Keep the latest known videos if the public feed is temporarily unavailable.
      });
  }, []);

  const notify = () => {
    toast.success("Merci, vous serez prévenu(e) pour la prochaine formation.", {
      position: "top-center",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
      theme: "colored",
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const sheetUrl = process.env.REACT_APP_FORMATION_SHEET_URL;
    const sheetRequest = sheetUrl
      ? fetch(sheetUrl, {
          method: "POST",
          mode: "no-cors",
          headers: {
            "Content-Type": "text/plain;charset=utf-8",
          },
          body: JSON.stringify({
            nom,
            email,
            message: objectif,
          }),
        })
      : Promise.resolve();

    Promise.all([
      client.create({
        _type: "inscriptionFormation",
        nom,
        email,
        objectif,
        submittedAt: new Date().toISOString(),
      }),
      sheetRequest,
      emailjs.sendForm(
        "service_2lyxfkm",
        "template_4wdnsuv",
        form.current,
        "ENWhELaCEiT_iLeD1"
      ),
    ])
      .then(() => {
        setNom("");
        setEmail("");
        setObjectif("");
        notify();
      })
      .catch(() => {
        toast.error("Une erreur est survenue. Merci de réessayer.", {
          position: "top-center",
          autoClose: 5000,
          theme: "colored",
        });
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  return (
    <div className="flex flex-col items-center w-full relative">
      <div className="flex flex-col w-full relative pb-20 lg:pb-6 px-8 lg:px-4 max-w-2xl lg:max-w-4xl 2xl:max-w-6xl ecran-2000:max-w-7xl">
        <MenuDesktopHorizontal />

        <div className="pt-24 lg:pt-6 relative flex flex-col items-center w-full text-white space-y-8 max-w-2xl lg:max-w-4xl 2xl:max-w-6xl ecran-2000:max-w-7xl">
          <ToastContainer />

          <div className="w-full flex flex-col items-center text-center space-y-4">
            <span
              className="texte-box-jaune text-4xl relative text-[#ffcb6b] mt-[0!important] tracking-tighter font-semibold font-[Yanone Kaffeesatz] after:content-['']
                after:absolute after:block after:h-1 after:bg-[#ffcb6b]
                after:w-full"
            >
              Animation
            </span>
            <p className="text-gray-300 text-base max-w-2xl leading-relaxed">
              Je réalise des animations 2D avec Adobe Animate pour donner vie à des personnages, des histoires
              et des idées créatives.
            </p>
          </div>

          <section className="w-full space-y-5">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
              <span className="text-3xl text-[#b8f2e6] font-semibold tracking-tighter font-[Yanone Kaffeesatz]">
                Vidéos récentes
              </span>
              <a
                href="https://www.youtube.com/@animationamara"
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center justify-center rounded-full border border-[#b8f2e6]/60 px-4 py-2 text-sm text-[#b8f2e6] transition hover:bg-[#b8f2e6]/10"
              >
                Voir ma chaîne YouTube
              </a>
            </div>

            <div className="grid gap-5 md:grid-cols-2">
              {videos.map((video) => (
                <a
                  key={video.videoId}
                  href={video.link || `https://www.youtube.com/watch?v=${video.videoId}`}
                  target="_blank"
                  rel="noreferrer"
                  className="group relative overflow-hidden rounded-2xl border border-gray-700 bg-[#1f2d32] shadow-lg transition duration-300 hover:-translate-y-1 hover:border-[#b8f2e6]/70"
                >
                  <div className="relative">
                    <img
                      src={video.thumbnail || `https://img.youtube.com/vi/${video.videoId}/maxresdefault.jpg`}
                      alt={video.title}
                      className="h-52 w-full object-cover transition duration-300 group-hover:scale-105"
                    />
                    <span className="absolute inset-0 z-10 origin-top-left scale-0 bg-[#b8f2e6] opacity-30 transition-transform duration-500 ease-out group-hover:scale-105" />
                    <FaPlus className="absolute left-1/2 top-1/2 z-20 hidden h-8 w-8 -translate-x-1/2 -translate-y-1/2 text-[#b8f2e6] group-hover:block" />
                    <div className="absolute left-4 top-4 z-20 rounded-full bg-[#0f1b1f]/70 px-3 py-1 text-[10px] uppercase tracking-[0.2em] text-[#b8f2e6]">
                      {video.category}
                    </div>
                  </div>

                  <div className="space-y-3 p-4">
                    <h3 className="text-xl font-semibold text-white">{video.title}</h3>
                    <p className="text-sm text-gray-300 leading-relaxed">{video.description}</p>
                    <span className="inline-flex items-center gap-2 text-sm font-medium text-[#b8f2e6]">
                      Regarder la vidéo
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={1.5}
                        stroke="currentColor"
                        className="h-4 w-4"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M13.5 4.5L21 12l-7.5 7.5M3 12h17.25"
                        />
                      </svg>
                    </span>
                  </div>
                </a>
              ))}
            </div>

            <div className="flex justify-center pt-1">
              <a
                href="https://www.youtube.com/@animationamara/videos"
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center justify-center rounded-full bg-[#b8f2e6] px-5 py-3 text-sm font-semibold text-[#1f2d32] transition hover:brightness-110"
              >
                Voir les autres vidéos sur ma chaîne
              </a>
            </div>
          </section>

          <section className="w-full rounded-2xl border border-gray-700 bg-[#1f2d32]/80 p-5 md:p-6">
            <div className="mb-5">
              <span className="text-3xl text-[#ffcb6b] font-semibold tracking-tighter font-[Yanone Kaffeesatz]">
                Formation à venir
              </span>
            </div>

            <div className="grid gap-5 lg:grid-cols-[1.2fr_0.8fr]">
              <div className="space-y-4 text-gray-300">
                <p>
                  Je prépare une prochaine formation dédiée à l&apos;animation 2D avec Adobe Animate, pour apprendre
                  à donner vie à des personnages, des scènes et des histoires.
                </p>
                <ul className="space-y-3 text-sm">
                  <li className="flex items-start gap-3">
                    <span className="mt-1 h-2.5 w-2.5 rounded-full bg-[#ffcb6b]" />
                    Découverte des bases du dessin et de l&apos;animation 2D.
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="mt-1 h-2.5 w-2.5 rounded-full bg-[#ffcb6b]" />
                    Création et animation de personnages avec Adobe Animate.
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="mt-1 h-2.5 w-2.5 rounded-full bg-[#ffcb6b]" />
                    Mise en scène, mouvements et storytelling à travers des projets pratiques.
                  </li>
                </ul>
              </div>

              <form ref={form} onSubmit={handleSubmit} className="flex flex-col space-y-3">
                <input
                  type="text"
                  className="w-full h-12 bg-transparent rounded-md pl-4 outline-none border-2 border-gray-500 focus:border-[#ffcb6b]"
                  placeholder="Nom"
                  name="from_name"
                  value={nom}
                  onChange={(e) => setNom(e.target.value)}
                  required
                />
                <input
                  type="email"
                  className="w-full h-12 bg-transparent rounded-md pl-4 outline-none border-2 border-gray-500 focus:border-[#ffcb6b]"
                  placeholder="Email"
                  name="reply_to"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
                <textarea
                  className="w-full h-28 bg-transparent rounded-md pl-4 pt-3 outline-none border-2 border-gray-500 focus:border-[#ffcb6b]"
                  placeholder="Pourquoi souhaitez-vous suivre cette formation ?"
                  name="message"
                  value={objectif}
                  onChange={(e) => setObjectif(e.target.value)}
                  required
                />
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="bg-[#ffcb6b] h-12 rounded-md text-lg text-[#1f2d32] font-semibold cursor-pointer transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  Être prévenu(e)
                </button>
              </form>
            </div>
          </section>
        </div>

        <div className="lg:hidden pt-4">
          <Footer />
        </div>
      </div>
    </div>
  );
};

export default Animation;
